<template>
  <transition name="ll-drawer">
    <div v-if="open" class="ll-drawer" :style="{ width: width + 'px' }">
      <div class="ll-drawer__header">
        <div class="ll-drawer__title">
          <i class="fa-duotone fa-messages"></i>
          <span>LoanLaunch Chat</span>

          <span v-if="unreadTotal > 0" class="ll-drawer__badge">
            {{ unreadTotal }}
          </span>
        </div>

        <button class="ll-drawer__close" type="button" @click="$emit('close')">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <div class="ll-drawer__body">
        <div class="loanlaunch-search">
          <i class="fa-solid fa-magnifying-glass loanlaunch-search__icon"></i>

          <input
            v-model="searchText"
            class="loanlaunch-search__input"
            type="text"
            placeholder="Search chat…"
          />

          <button
            v-if="searchText"
            class="loanlaunch-search__clear"
            @click="clearSearch"
            title="Clear"
            type="button"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div ref="listShell" class="loanlaunch-listview-shell" @scroll="onShellScroll">
          <ListView :data-items="filteredMessages" :item="'chats'" class="loanlaunch-listview">
            <template #chats="{ props }">
              <div
                :key="props.dataItem.id"
                @click="$emit('message-click', props.dataItem)"
                :class="[
                  'loanlaunch-message',
                  { 'loanlaunch-message--unread': !props.dataItem.isread }
                ]"
              >
                <div class="loanlaunch-message__top">
                  <span class="loanlaunch-message__name">{{ props.dataItem.username }}</span>
                  <span
                    class="loanlaunch-message__date"
                    :title="formatDateTimeShort(props.dataItem.sent)"
                  >
                    {{ toFuzzyTime(props.dataItem.sent) }}
                    <span v-if="!props.dataItem.isread" class="loanlaunch-message__unread-indicator">
                      &#8226;
                    </span>
                  </span>
                </div>

                <div class="loanlaunch-message__bottom" :title="props.dataItem.messagetext">
                  <span class="loanlaunch-message__content">{{ props.dataItem.messagetext }}</span>
                </div>
              </div>
            </template>
          </ListView>

          <div v-if="!filteredMessages?.length && searchText" class="loanlaunch-empty">
            No messages match “{{ searchText }}”
          </div>

          <div v-if="!messages?.length && isFetching" class="loanlaunch-loader">
            <i class="fa-solid fa-spinner-third fa-spin" style="--fa-animation-duration: 1s;"></i>
          </div>
        </div>

        <div v-if="messages?.length > 0 && isFetching" class="loanlaunch-loader loanlaunch-loader--bottom">
          <i class="fa-solid fa-spinner-third fa-spin" style="--fa-animation-duration: 1s;"></i>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ListView } from "@progress/kendo-vue-listview";
import { formatDateTimeShort, toFuzzyTime } from "@/helpers/formatters.js";

export default {
  name: "LoanLaunchChatDrawer",
  components: { ListView },
  props: {
    open: { type: Boolean, default: false },
    width: { type: Number, default: 420 },
    messages: { type: Array, default: () => [] },
    unreadTotal: { type: Number, default: 0 },
    isFetching: { type: Boolean, default: false }
  },
  emits: ["close", "fetch-more", "fetch-newer", "message-click", "search"],
  data() {
    return {
      searchText: ""
    };
  },
  computed: {
    filteredMessages() {
      const q = (this.searchText || "").trim().toLowerCase();
      if (!q) return this.messages;

      return (this.messages || []).filter((m) => {
        const u = (m.username || "").toLowerCase();
        const t = (m.messagetext || "").toLowerCase();
        const id = String(m.loanid || m.id || "").toLowerCase();
        return u.includes(q) || t.includes(q) || id.includes(q);
      });
    }
  },
  watch: {
    searchText(val) {
      this.$emit("search", val);
    },
    open(val) {
      if (!val) return;
      this.$nextTick(() => {
        const el = this.$refs.listShell;
        if (el) el.scrollTop = 0;
      });
    }
  },
  methods: {
    formatDateTimeShort,
    toFuzzyTime,

    clearSearch() {
      this.searchText = "";
    },

    onShellScroll() {
      const el = this.$refs.listShell;
      if (!el) return;

      const isNearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 12;
      if (isNearBottom) this.$emit("fetch-more");
    }
  }
};
</script>

<style scoped>
.ll-drawer {
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  z-index: 18000;
  background: #fff;
  border-left: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: -18px 0 34px rgba(15, 23, 42, 0.12);
  display: flex;
  flex-direction: column;
}
.ll-drawer__header {
  height: 58px;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}
.ll-drawer__title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 950;
  color: #0f172a;
  font-size: 14px;
}
.ll-drawer__badge {
  background: rgba(37, 99, 235, 0.12);
  color: #2563eb;
  border: 1px solid rgba(37, 99, 235, 0.25);
  font-weight: 950;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 999px;
}
.ll-drawer__close {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.ll-drawer__close:hover {
  background: rgba(15, 23, 42, 0.04);
}
.ll-drawer__body {
  padding: 12px;
  min-height: 0;
  height: calc(100vh - 58px);
  display: flex;
  flex-direction: column;
}
.ll-drawer-enter-active,
.ll-drawer-leave-active {
  transition: transform 0.18s ease, opacity 0.18s ease;
}
.ll-drawer-enter-from,
.ll-drawer-leave-to {
  transform: translateX(18px);
  opacity: 0;
}
.loanlaunch-listview-shell {
  flex: 1;
  min-height: 0;
}
</style>

<style src="@/assets/css/commandcenter/commandcenter.css"></style>
